import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@/translations';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  TreeSelect,
} from 'antd';
import {
  AppRoles,
  AppUserTypes,
  CustomerHierarchy,
  CustomerInspectors,
  Location,
  NovuWorkflow,
  SelectOption,
  User,
  UserCustomerModel,
  UserTypeRoles,
} from '@/types';
import { faClose, faMinus, faPlus, Icon } from '@/ui/components';
import clonedeep from 'lodash.clonedeep';
import { useResetUserPassword } from '@/api/Users';
import { useEmailNotificationRoleAccess } from '@/api/EmailNotifications';
import { useAppCustomers } from '@/hooks';

type UserPopupProps = {
  data?: User;
  userTypeRoles?: UserTypeRoles[];
  customers?: CustomerInspectors[];
  customersHierarchy?: CustomerHierarchy[];
  locations?: Location[];
  onSubmit(user: User): Promise<string | undefined>;
  onCancel(): void;
};

export const UserPopup: FC<UserPopupProps> = ({
  data,
  userTypeRoles,
  customers,
  customersHierarchy,
  locations,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<User>();
  const { hasSuperAdminAccess } = useAppCustomers();

  const [loading, setLoading] = useState(false);
  const [userCustomers, setUserCustomers] = useState<UserCustomerModel[]>([]);
  const [userType, setUserType] = useState<AppUserTypes>();
  const [userRoles, setUserRoles] = useState<SelectOption<AppRoles>[]>([]);
  const [isTmsUser, setIsTmsUser] = useState<boolean>(false);
  const [userTypeCustomerHierarchy, setUserTypeCustomerHierarchy] = useState<
    CustomerHierarchy[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { mutateAsync: resetUserPasswordAsync } = useResetUserPassword();
  const { data: roleAccessRows } = useEmailNotificationRoleAccess();
  const watchedRoles = Form.useWatch('roles', form) as AppRoles[] | undefined;

  const isAdd = data && !data.id;

  const availableEmailNotifications = useMemo(() => {
    if (!userType || !watchedRoles?.length) return [];
    const map = new Map<
      NovuWorkflow,
      { novuWorkflow: NovuWorkflow; description: string }
    >();
    (roleAccessRows || [])
      .filter(
        (r) =>
          r.userType === userType &&
          watchedRoles.includes(r.userRole as AppRoles),
      )
      .forEach((r) => {
        if (!map.has(r.novuWorkflow)) {
          map.set(r.novuWorkflow, {
            novuWorkflow: r.novuWorkflow,
            description: r.description || r.novuWorkflow,
          });
        }
      });
    return Array.from(map.values());
  }, [roleAccessRows, userType, watchedRoles]);

  const handleSubmit = useCallback(
    async (user: User) => {
      setErrorMessage(undefined);
      if (userType !== AppUserTypes.InternalUsers && !userCustomers.length) {
        setErrorMessage('Please setup at least one customer for the user.');
        return;
      }

      setLoading(true);
      user.customers = userCustomers;
      const enabled =
        (form.getFieldValue('enabledEmailNotifications') as NovuWorkflow[]) ||
        [];
      const enabledSet = new Set(enabled);
      user.emailNotificationPreferences = availableEmailNotifications.map(
        (w) => ({
          novuWorkflow: w.novuWorkflow,
          isEnabled: enabledSet.has(w.novuWorkflow),
        }),
      );
      const error = await onSubmit(user);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [
      availableEmailNotifications,
      form,
      onCancel,
      onSubmit,
      userCustomers,
      userType,
    ],
  );

  const changeUserType = useCallback(
    (value?: AppUserTypes) => {
      setUserCustomers([]);
      setUserType(value);
      const allRoles =
        userTypeRoles?.find((o) => o.value === value)?.userRoles || [];
      const user = form.getFieldsValue();
      const filteredRoles = (user?.roles || []).filter((r) =>
        allRoles.some((o) => o.value === r),
      );
      form.setFieldsValue({ roles: filteredRoles });
      setUserRoles(allRoles);
    },
    [form, userTypeRoles],
  );

  const checkIsChild = useCallback(
    (list: CustomerHierarchy[], customerId: number) =>
      list.some((item) => {
        if (item.customerId === customerId) return true;
        if (item.children) {
          const result = checkIsChild(item.children, customerId);
          if (result) return true;
        }
        return false;
      }),
    [],
  );

  const getRootCustomer = useCallback(
    (list: CustomerHierarchy[], customerId: number) => {
      const checked = list.find((item) => {
        if (item.customerId === customerId) return true;
        if (item.children && checkIsChild(item.children, customerId))
          return true;
        return false;
      });
      return checked?.customerId;
    },
    [checkIsChild],
  );

  useEffect(() => {
    if (data) data.locationIds = data.locations?.map((o) => o.value);
    changeUserType(data?.userType);
    setIsTmsUser(data?.isTmsUser || false);
    setUserCustomers(
      (data?.customers || []).map((c) => {
        if (c.customerId)
          c.rootCustomerId = getRootCustomer(
            customersHierarchy || [],
            c.customerId,
          );
        return c;
      }),
    );
    setErrorMessage(undefined);
    form.resetFields();
    if (data) {
      form.setFieldsValue({
        ...data,
        enabledEmailNotifications: (data.emailNotificationPreferences || [])
          .filter((p) => p.isEnabled)
          .map((p) => p.novuWorkflow),
      } as User & { enabledEmailNotifications?: NovuWorkflow[] });
    }
  }, [changeUserType, customersHierarchy, data, form, getRootCustomer]);

  useEffect(() => {
    const current =
      (form.getFieldValue('enabledEmailNotifications') as NovuWorkflow[]) || [];
    const allowed = new Set(
      availableEmailNotifications.map((w) => w.novuWorkflow),
    );
    form.setFieldValue(
      'enabledEmailNotifications',
      current.filter((w) => allowed.has(w)),
    );
  }, [availableEmailNotifications, form]);

  useEffect(() => {
    setUserTypeCustomerHierarchy(
      clonedeep(customersHierarchy || []).map((c: CustomerHierarchy) => {
        if (userType !== AppUserTypes.SubCustomers) {
          c.children = [];
        } else {
          c.disabled = true;
        }
        return c;
      }),
    );
  }, [customersHierarchy, userType]);

  const resetUserPassword = () => {
    if (!data?.id) return;

    modal.confirm({
      title: 'Confirm',
      content: `Are you sure you want to reset this user's password? The new password will be sent to the user's email.`,
      okText: t('yes'),
      onOk: async () => resetUserPasswordAsync(data.id),
      onCancel: () => {},
    });
  };

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} ${t('user')}`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {hasSuperAdminAccess && !isAdd ? (
            <Button type="primary" onClick={resetUserPassword}>
              {t('resetPassword')}
            </Button>
          ) : null}
          <CancelBtn />
          <OkBtn />
        </>
      )}
      forceRender
      width={{
        xs: '90%',
        sm: '80%',
        md: '76%',
        lg: '70%',
        xl: '65%',
        xxl: '60%',
      }}
    >
      <Spin spinning={loading}>
        <Form
          id="user-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ flex: '0 0 150px' }}
          autoComplete="off"
        >
          <Form.Item<User> name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item<User>
            label={t('username')}
            name="userName"
            required
            rules={[{ required: true }]}
          >
            <Input disabled={!isAdd} />
          </Form.Item>

          <Form.Item<User>
            label={t('displayName')}
            name="displayName"
            required
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item<User>
            label={t('userType')}
            name="userType"
            rules={[{ required: true }]}
          >
            <Select
              options={userTypeRoles?.map((o) => ({
                label: o.name,
                value: o.value,
              }))}
              allowClear
              onChange={(e) => changeUserType(e)}
            />
          </Form.Item>

          <Form.Item<User>
            label={t('userRoles')}
            name="roles"
            rules={[{ required: true }]}
          >
            <Select
              allowClear
              mode="multiple"
              options={userRoles?.map((o) => ({
                label: o.text,
                value: o.value,
              }))}
            />
          </Form.Item>

          {userType === AppUserTypes.InternalUsers ? (
            <>
              <Form.Item<User>
                label={t('tmsAccess')}
                name="isTmsUser"
                valuePropName="checked"
              >
                <Checkbox onChange={(e) => setIsTmsUser(e.target.checked)} />
              </Form.Item>

              {isAdd && isTmsUser ? (
                <Form.Item<User>
                  label={t('password')}
                  name="password"
                  required
                  rules={[{ required: true }]}
                >
                  <Input type="password" />
                </Form.Item>
              ) : null}
            </>
          ) : null}

          {isTmsUser ? (
            <Form.Item label="Inspectors Mappings">
              <div className="d-flex align-items-center justify-content-end gap-3 mb-3">
                {userCustomers.length ? (
                  <Button
                    size="small"
                    shape="circle"
                    color="danger"
                    variant="outlined"
                    onClick={() => {
                      if (userCustomers.some((o) => o.customerId)) {
                        modal.confirm({
                          title: 'Confirm',
                          content:
                            'Are you sure you want to clear all the inspecors?',
                          okText: t('yes'),
                          onOk: async () => setUserCustomers([]),
                          onCancel: () => {},
                        });
                      } else {
                        setUserCustomers([]);
                      }
                    }}
                    title="Clear All"
                  >
                    <Icon icon={faClose} />
                  </Button>
                ) : null}
                <Button
                  size="small"
                  shape="circle"
                  title="Add new"
                  onClick={() => {
                    const invalid = userCustomers.some((o) => !o.customerId);
                    if (invalid) {
                      message.warning(
                        'Please setup the customer before add new one',
                      );
                    } else {
                      setUserCustomers([...userCustomers, {}]);
                    }
                  }}
                >
                  <Icon icon={faPlus} />
                </Button>
              </div>

              {userCustomers.length ? (
                <Space direction="vertical" className="w-100">
                  {userCustomers.map((item, idx) => (
                    <div
                      key={item.customerId}
                      className="d-flex align-items-center flex-row-reverse"
                    >
                      <Button
                        size="small"
                        shape="circle"
                        color="danger"
                        variant="outlined"
                        onClick={() =>
                          setUserCustomers(
                            userCustomers.filter((_, j) => idx !== j),
                          )
                        }
                      >
                        <Icon icon={faMinus} />
                      </Button>
                      <div className="flex-fill d-flex">
                        <span style={{ lineHeight: '40px' }}>{idx + 1}.</span>
                        <Row className="flex-fill mx-2" gutter={[10, 10]}>
                          <Col span={12}>
                            <TreeSelect
                              className="w-100"
                              placeholder="Choose Customer"
                              showSearch
                              treeData={userTypeCustomerHierarchy}
                              fieldNames={{
                                label: 'customerName',
                                value: 'customerId',
                              }}
                              treeNodeFilterProp="customerName"
                              value={item.customerId}
                              onChange={(value) => {
                                const copy = [...userCustomers];
                                if (
                                  copy.some(
                                    (c, cidx) =>
                                      cidx !== idx && c.customerId === value,
                                  )
                                ) {
                                  message.warning('Customer already added');
                                  return;
                                }
                                copy[idx].customerId = value;
                                copy[idx].rootCustomerId = getRootCustomer(
                                  customersHierarchy || [],
                                  value,
                                );
                                setUserCustomers(copy);
                              }}
                            />
                          </Col>
                          <Col md={12}>
                            <Select
                              className="w-100"
                              placeholder="Choose Inspector"
                              showSearch
                              options={(
                                customers?.find(
                                  (c) => c.customerId === item.rootCustomerId,
                                )?.inspectors || []
                              ).map((o) => ({
                                label: o.name,
                                value: o.id,
                              }))}
                              optionFilterProp="label"
                              value={item.inspectorId}
                              onChange={(value) => {
                                const copy = [...userCustomers];
                                copy[idx].inspectorId = value;
                                setUserCustomers(copy);
                              }}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  ))}
                </Space>
              ) : null}
            </Form.Item>
          ) : userType !== AppUserTypes.InternalUsers ? (
            <Form.Item label="Customer Mappings">
              <TreeSelect
                className="w-100"
                placeholder="Choose Customer"
                showSearch
                multiple
                treeData={userTypeCustomerHierarchy}
                fieldNames={{
                  label: 'customerName',
                  value: 'customerId',
                }}
                treeNodeFilterProp="customerName"
                value={userCustomers.map((o) => o.customerId)}
                onChange={(value) => {
                  const copy = value.map((v) => ({
                    customerId: v,
                    rootCustomerId: getRootCustomer(
                      customersHierarchy || [],
                      v || 0,
                    ),
                  }));
                  setUserCustomers(copy);
                }}
              />
            </Form.Item>
          ) : null}

          <Form.Item<User> label="Site Permissions" name="locationIds">
            <Select
              allowClear
              mode="multiple"
              placeholder="Choose Sites"
              showSearch
              optionFilterProp="label"
              options={locations?.map((o) => ({
                value: o.id,
                label: o.desc,
              }))}
            />
          </Form.Item>

          {!isAdd ? (
            <Form.Item<User>
              label={t('active')}
              name="active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          ) : null}

          <Form.Item<User>
            label={t('receiveEmail')}
            name="receiveEmail"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {availableEmailNotifications.length > 0 ? (
            <Form.Item
              label={t('emailNotificationPreferences')}
              name="enabledEmailNotifications"
            >
              <Checkbox.Group
                options={availableEmailNotifications.map((w) => ({
                  label: w.description,
                  value: w.novuWorkflow,
                }))}
              />
            </Form.Item>
          ) : null}

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
